import { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Modal,
  Skeleton,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useMediaQuery } from "react-responsive";
import { CapSteps } from "../../constants/capConstants";
import { useCapContext } from "../../providers/CapContext";
import { StepName } from "../../capTypes";
import { scrollToCapStep } from "../../hooks/useCapScrollSpy";
import { FryProps, HamburgerMenu } from "./HamburgerMenu";
import { getCapStickyNavOffset } from "./CapStepSection";
import { scrollActiveTabIntoView } from "./capNavScroll";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";

type CapStepConfig = (typeof CapSteps)[number];

type CapSimplifiedNavProps = {
  currentStep: StepName;
  changeStep: (step: StepName) => void;
  isLoading: boolean;
  visibleSteps: CapStepConfig[];
  isCompact: boolean;
  isStickyNavPinned: boolean;
};

const CapSimplifiedNav = ({
  currentStep,
  changeStep,
  isLoading,
  visibleSteps,
  isCompact,
  isStickyNavPinned,
}: CapSimplifiedNavProps) => {
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const stepsListRef = useRef<HTMLDivElement>(null);
  const navOffset = getCapStickyNavOffset(isCompact);
  const goToStep = (step: StepName) => {
    changeStep(step);
    scrollToCapStep(step, navOffset);
  };

  const [warning, setWarning] = useState<Error | null>(null);
  const [acknowledgeRevisitOP, setAcknowledgeRevisitOP] =
    useState<boolean>(false);

  const {
    sow,
    prospect,
    sfdcUrl,
    oneBaseUrl,
    splatBaseUrl,
    reviewOfferId,
    capTask,
    isCustomer,
  } = useCapContext();

  const projectNotesUrl = `${sfdcUrl}/apex/wfproject_lwc?id=${sow?.projectId}&sfdc.override=1&c__tabName=notes`;
  const customerProfileUrl = `${oneBaseUrl}/customers/customer-profile/${prospect?.prospectId}`;
  const revisitOfferPresentation = `${splatBaseUrl}/go-solar/${prospect?.prospectId}/offer/${reviewOfferId}`;
  const capInSfdcUrl = `${sfdcUrl}/apex/wftask_lwc?id=${capTask?.id}&sfdc.override=1&c__tabName=DETAILS`;
  const openNewPage = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const fries: FryProps[] = [
    {
      action: () => openNewPage(capInSfdcUrl),
      label: "CAP in Salesforce",
    },
    {
      action: () => openNewPage(capInSfdcUrl),
      label: "CAP changes needed",
    },
    {
      action: () => openNewPage(projectNotesUrl),
      label: "Project notes",
    },
    {
      action: () => openNewPage(customerProfileUrl),
      label: "Customer profile",
    },
    {
      action: () => {
        setWarning(new Error("Offer presentation is for reference only"));
        setAcknowledgeRevisitOP(false);
      },
      label: "Revisit offer presentation",
    },
  ];

  const filteredCapSteps = visibleSteps;

  useEffect(() => {
    if (isLoading || !stepsListRef.current) {
      return;
    }

    const activeTab = stepsListRef.current.querySelector(
      `[data-cap-nav-step="${currentStep}"]`
    );

    if (activeTab instanceof HTMLElement) {
      scrollActiveTabIntoView(stepsListRef.current, activeTab);
    }
  }, [currentStep, isLoading]);

  const getSteps = () => {
    let steps = filteredCapSteps.map(({ order }) => (
      <LoadingSkeletonSteps key={order} />
    ));

    if (!isLoading) {
      steps = filteredCapSteps.map((capStep) => (
        <CapNavItem
          key={capStep.order}
          data-cap-nav-step={capStep.name}
          isCompact={isCompact}
          onClick={() => goToStep(capStep.name)}
          style={{
            borderRadius: "unset",
            borderBottom:
              capStep.name === currentStep
                ? `3px solid ${tokens.BRAND_HERO_BLUE}`
                : "none",
          }}
        >
          <Typography
            size={18}
            fontWeight={
              capStep.name === currentStep
                ? isStickyNavPinned
                  ? "bold"
                  : "medium"
                : "regular"
            }
            color={
              capStep.name === currentStep
                ? tokens.BRAND_HERO_BLUE
                : isStickyNavPinned
                ? tokens.BLACK
                : tokens.TINTS_HERO_BLUE_60
            }
            lineHeight={1.2}
          >
            {capStep.name}
          </Typography>
        </CapNavItem>
      ));
    }
    return steps;
  };

  const [showScrollLeftOption, setShowScrollLeftOption] = useState(false);
  const shouldHaveScrollOptions = isMobile && filteredCapSteps.length > 3;

  const scrollNav = (direction: "left" | "right") => {
    stepsListRef.current?.scrollBy({
      left: direction === "right" ? 300 : -300,
      behavior: "smooth",
    });

    setShowScrollLeftOption(direction === "right" ? true : false);
  };

  const modalPrimaryButton = {
    text: "Continue",
    onClick: () => {
      openNewPage(revisitOfferPresentation);
      setWarning(null);
    },
    testId: "revisit-offer-presentation-modal-continue-button",
    ariaLabel: "revisit offer presentation modal continue button",
    disabled: !acknowledgeRevisitOP,
    isLoading: false,
  };

  const modalSecondaryButton = {
    text: "Cancel",
    onClick: () => setWarning(null),
    testId: "revisit-offer-presentation-modal-cancel-button",
    ariaLabel: "revisit offer presentation modal cancel button",
    disabled: false,
  };

  if (warning) {
    return (
      <Modal
        title="Offer presentation is for reference only"
        type="destructive"
        onClose={() => setWarning(null)}
        primaryButton={modalPrimaryButton}
        secondaryButton={modalSecondaryButton}
        testId="revisit-offer-presentation-modal"
        aria-label="revisit offer presentation modal"
      >
        <Grid>
          <Typography color={tokens.TINTS_OFF_WHITE_20}>
            <ul>
              <li>
                For post-sale design changes, reach out to SSD or Design
                Engineering
              </li>
              <li>
                For post-sale pricing changes, use the "Edit pricing" feature
                from the Terms section of Design tab
              </li>
            </ul>
          </Typography>
          <Checkbox
            label="I understand that offer presentation is for reference purposes"
            onClick={() => setAcknowledgeRevisitOP(!acknowledgeRevisitOP)}
          />
        </Grid>
      </Modal>
    );
  }

  return (
    <TabNav isCompact={isCompact}>
      {shouldHaveScrollOptions && showScrollLeftOption && (
        <ScrollButton onClick={() => scrollNav("left")}>
          <SvgIcon
            name={SvgNames.ChevronLeft}
            width={18}
            height={18}
            color={tokens.HEROBLUE_70}
          />
        </ScrollButton>
      )}
      <StepsList
        ref={stepsListRef}
        id="steps-list"
        isMobile={isMobile}
        showRightScrollStyles={shouldHaveScrollOptions}
      >
        {getSteps()}
      </StepsList>
      {shouldHaveScrollOptions && !isLoading && (
        <ScrollButton onClick={() => scrollNav("right")}>
          <SvgIcon
            name={SvgNames.ChevronRight}
            width={18}
            height={18}
            color={tokens.HEROBLUE_70}
          />
        </ScrollButton>
      )}
      {isCustomer ? (
        <></>
      ) : isLoading ? (
        <CircleSkeleton />
      ) : (
        <HamburgerWrapper>
          <HamburgerMenu fries={fries} />
        </HamburgerWrapper>
      )}
    </TabNav>
  );
};

const HamburgerWrapper = styled.div`
  margin-left: auto;
  padding-right: 24px;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const LoadingSkeletonSteps = styled(Skeleton)`
  width: 76px;
  height: 17px;
`;

const CircleSkeleton = styled(Skeleton)`
  height: 32px;
  width: 32px;
  border-radius: 16px;
`;

const CapNavItem = styled(Button)<{ isCompact: boolean }>`
  display: flex;
  min-width: fit-content;
  background: none;
  padding: 8px;
`;

const StepsList = styled.div<{
  isMobile: boolean;
  showRightScrollStyles: boolean;
}>`
  display: flex;
  gap: ${({ isMobile }) => (isMobile ? "12px" : "24px")};
  width: ${({ isMobile }) => (isMobile ? "auto" : "90%")};
  overflow: scroll;
  height: 32px;

  ::-webkit-scrollbar {
    height: 0px;
    width: 0px;
  }
`;

const ScrollButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
`;

const TabNav = styled.nav<{ isCompact: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${({ isCompact }) => (isCompact ? "4px 0" : "8px 0")};
`;

const Grid = styled.div`
  display: grid;
  gap: 24px;
`;

export { CapSimplifiedNav };
